import { useState, useCallback, useEffect } from "react";
import { WorkflowsAPI } from "../connections/Workflows";
import usePolling from "./usePolling";
import { POLLING_INTERVALS } from "../config/polling";

/**
 * usePagerDutyActivity - Hook for fetching and polling PagerDuty activity data
 *
 * Uses the project's standard background-refresh pattern: initial load shows a
 * loading spinner; subsequent background polls keep data live without replacing
 * visible content with a spinner.
 *
 * Polling pauses when the browser tab is hidden and stops automatically when
 * usePolling's built-in backoff/failure ceiling is hit.
 *
 * @param {string|number} problemID  - Problem record number (e.g. problem.number)
 * @param {string|number} outageID   - Outage record number (e.g. outage.number)
 * @param {boolean}       isActive   - Pass false (resolved incidents) to stop polling
 *
 * @returns {{ data, loading, refreshing, lastUpdated, error, refresh }}
 */
function usePagerDutyActivity(problemID, outageID, isActive = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const fetchActivity = useCallback(
    async (isBackgroundRefresh = false) => {
      if (!problemID || !outageID) {
        if (!isBackgroundRefresh) setLoading(false);
        return;
      }

      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const result = await WorkflowsAPI.getPagerDutyActivity(
          problemID,
          outageID
        );
        setData(result);
        setLastUpdated(new Date());
        if (!isBackgroundRefresh) setError(null);
      } catch (err) {
        if (isBackgroundRefresh) {
          // Keep existing data visible; log for diagnostics but don't surface to user.
          console.warn("[usePagerDutyActivity] Background refresh failed:", err);
          // Re-throw so usePolling can track consecutive failures and apply backoff.
          throw err;
        } else {
          setError(err.message || "Failed to load PagerDuty activity.");
          setData(null);
        }
      } finally {
        if (isBackgroundRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
          setInitialLoadDone(true);
        }
      }
    },
    [problemID, outageID]
  );

  // Initial load when IDs become available
  useEffect(() => {
    if (!problemID || !outageID) {
      setLoading(false);
      return;
    }
    fetchActivity(false);
  }, [problemID, outageID, fetchActivity]);

  // Background polling — only while the incident is active and initial load has completed
  usePolling(
    () => fetchActivity(true),
    POLLING_INTERVALS.PD_ACTIVITY,
    {
      enabled: isActive && initialLoadDone,
      pauseWhenHidden: true,
    }
  );

  const refresh = useCallback(() => fetchActivity(false), [fetchActivity]);

  return { data, loading, refreshing, lastUpdated, error, refresh };
}

export default usePagerDutyActivity;
